import React, { useEffect, useMemo, useState, useRef } from "react";
import { at } from "lodash";
import { getIn, useField, useFormikContext } from "formik";
import {
  Box,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { bloqcibeApi } from "../../../store/slices/apiSlice";
import { setTempParticipantId } from "../../../store/slices/subjectSlice";
import moment from "moment";
import dayjs from "dayjs";
import { parseIntOrZero } from "../../../util/util";
import { calculateDiagnosisDurationYears, isNewValidationEnabled, getDiagnosisDateInfoFromValues } from "../utils/validationService";

export default function InputField(props) {
  const { name, 
    errorText,
    fieldKey,
    required,
    requiredDepends,
    requiredValidationValue,
    requiredDependOnFields,
    fieldConfig,
    userSiteTrialRole,
    ...rest } = props;
  const [field, meta, helpers] = useField(props);
  const { trialId, trialSiteId, siteInitStep, subjectMasterId } = useParams();
  const dispatch = useDispatch();
  const sponsorId = useSelector((state) => state.auth.sponsorId);
  const { values } = useFormikContext();
  const subject = useSelector((state) => state.subject)
  const trialSite = useSelector((state) => state.trial?.trialSiteDetail)
  const [getSubjectAnswers] = bloqcibeApi.endpoints.getSubjectAnswers.useLazyQuery();
  const [getSubjectTempParticipantId] = bloqcibeApi.endpoints.getSubjectTempParticipantId.useLazyQuery();
  const [disabled,setDisabled]=useState(false)
  const prevAgeAtIndexDateRef = useRef(null);
  // Cache for API responses
  const apiCacheRef = useRef(new Map());

  useEffect(() => {
    (async () => {
      if (fieldKey) {
        const siteId = trialSite?.siteTrialData?.site?.id;
        switch (fieldKey) {
          case 'participantId':
            const currentValue1 = getIn(values, name);
            if(!siteId) return;
            if (currentValue1) {
              return;
            }
            const participantIdCacheKey = `getSubjectAnswers_${sponsorId}_${trialId}_${siteId}_${subjectMasterId}_${fieldConfig?.dependStepKey}_${fieldConfig?.dependSectionKey}_${fieldConfig?.dependSubSectionKey}_${fieldConfig?.dependCategoryKey}_${fieldConfig?.dependFieldKey}`;
            let _answers;
            if (apiCacheRef.current.has(participantIdCacheKey)) {
              _answers = apiCacheRef.current.get(participantIdCacheKey);
            } else {
              _answers = await getSubjectAnswers({
                sponsorId,
                trialId,
                siteId,
                payload: {
                  subjectMasterId: subjectMasterId,
                  stepKey: fieldConfig?.dependStepKey,
                  sectionKey: fieldConfig?.dependSectionKey,
                  subSectionKey: fieldConfig?.dependSubSectionKey,
                  categoryKey: fieldConfig?.dependCategoryKey,
                  fieldKey: fieldConfig?.dependFieldKey,
                },
              }).unwrap();
              apiCacheRef.current.set(participantIdCacheKey, _answers);
            }

            const ans = _answers?.[fieldConfig?.dependCategoryKey]?.[fieldConfig?.dependFieldKey];
            if (ans) helpers.setValue(ans);
            break;
          case 'siteNo':
            helpers.setValue(trialSite?.siteTrialData?.site?.id);
            setDisabled(true);
            break;
          case 'calculatedAge':
            let value = props?.valueKey ? getIn(values, props?.valueKey) : null;
            let age = null;
            if (value && value !== 'Invalid Date') {
              const birthdate = moment(value, 'DD/MM/YYYY');
              const currentDate = moment();
              let a = currentDate.diff(birthdate, 'years');
              if (typeof a === 'string' || typeof a === 'number') age = a;
            }
            if (age !== null) helpers.setValue(age);
            break;
          case 'bmi':
            let weight = getIn(values, 'demographic_data.weight');
            let height = getIn(values, 'demographic_data.height');
            let pi = null;

            if (!weight || !height || weight === '' || height === '') {
              helpers.setValue('');
              return;
            }

            if (weight && height) {
              let heightInMeters = height / 100;
              pi = weight / (heightInMeters * heightInMeters);
              pi = Math.ceil(pi * 100) / 100;
            }
            if (pi !== null) helpers.setValue(pi);
            else helpers.setValue('');
            break;
          case 'durationOfDiagnosis':
            let dateOfDiagnosis = getIn(values, 'pdl_Checklist.dateOfDiagnosis');
            let yearOfDiagnosis = getIn(values, 'pdl_Checklist.yearOfDiagnosis');
            let indexDate = getIn(values, 'pdl_Checklist.indexDate');

            const hasExactDate = dateOfDiagnosis && dateOfDiagnosis !== '';
            const hasYearOnly = !hasExactDate && yearOfDiagnosis && yearOfDiagnosis !== '';

            if (hasYearOnly) {
              helpers.setValue('N/A');
              break;
            }

            let rawDuration = null;
            if (hasExactDate && indexDate) {
              const parseDateValue = (value) => {
                if (!value) return null;
                const ddmmyyyy = dayjs(value, 'DD/MM/YYYY');
                if (ddmmyyyy.isValid()) return ddmmyyyy;
                const yyyymmdd = dayjs(value, 'YYYY-MM-DD');
                if (yyyymmdd.isValid()) return yyyymmdd;
                const fallback = dayjs(value);
                return fallback.isValid() ? fallback : null;
              };

              const diagnosisInfo = getDiagnosisDateInfoFromValues({
                dateOfDiagnosis,
                yearOfDiagnosis,
                indexDate
              });

              if (diagnosisInfo?.date) {
                const index = parseDateValue(indexDate);
                if (index && index.isValid()) {
                  rawDuration = index.diff(diagnosisInfo.date, 'year', true);
                }
              }
            }

            let displayDuration = null;
            if (rawDuration !== null) {
              const rounded = Math.round(rawDuration * 1000) / 1000;
              const clamped = rounded < 0 ? 0 : rounded;
              if (rawDuration >= 0.5 && rawDuration < 0.6) {
                displayDuration = clamped;
              } else if (rawDuration >= 8.000001 && rawDuration < 8.1) {
                displayDuration = clamped;
              } else {
                displayDuration = clamped;
              }
            } else {
              const fallback = calculateDiagnosisDurationYears({
                dateOfDiagnosis,
                yearOfDiagnosis,
                indexDate,
              });
              displayDuration = fallback !== null ? (Math.round(fallback * 100) / 100) : null;
              if (displayDuration !== null && displayDuration < 0) displayDuration = 0;
            }

            displayDuration !== null ? helpers.setValue(displayDuration) : helpers.setValue(0);
            break;
          case 'age':
          case 'occupation':
          case 'height':
          case 'weight':
          case 'weight_unit':
          case 'race_demographics_comment':
          case 'ethnicity_demographics_comment':
          case 'gender_demographics':
          case 'personTakingIC':
            let value1 = props?.valueKey ? getIn(subject?.answers, props?.valueKey) : '';
            if (value1) helpers.setValue(value1);
            break;
          case 'ageAtIndexDate':
            // Feature flag: Only run old validation if new validation system is disabled
            if (isNewValidationEnabled()) {
              break;
            }
            const ageAtIndexDate = getIn(values, name);
            // Only run validation if the value actually changed
            if (ageAtIndexDate !== prevAgeAtIndexDateRef.current) {
              prevAgeAtIndexDateRef.current = ageAtIndexDate;

              if (ageAtIndexDate) {
                try {
                  if (!siteId) return;
                  const ageAtIndexDateCacheKey = `getSubjectAnswers_${sponsorId}_${trialId}_${siteId}_${subjectMasterId}_${fieldConfig?.dependStepKey}_${fieldConfig?.dependSectionKey}_${fieldConfig?.dependSubSectionKey}_${fieldConfig?.dependCategoryKey}_${fieldConfig?.dependFieldKey}`;
                  let _answers;
                  if (apiCacheRef.current.has(ageAtIndexDateCacheKey)) {
                    _answers = apiCacheRef.current.get(ageAtIndexDateCacheKey);
                  } else {
                    _answers = await getSubjectAnswers({
                      sponsorId,
                      trialId,
                      siteId,
                      payload: {
                        subjectMasterId: subjectMasterId,
                        stepKey: fieldConfig?.dependStepKey,
                        sectionKey: fieldConfig?.dependSectionKey,
                        subSectionKey: fieldConfig?.dependSubSectionKey,
                        categoryKey: fieldConfig?.dependCategoryKey,
                        fieldKey: fieldConfig?.dependFieldKey,
                      },
                    }).unwrap();
                    apiCacheRef.current.set(ageAtIndexDateCacheKey, _answers);
                  }

                  const ans = _answers?.[fieldConfig?.dependCategoryKey]?.[fieldConfig?.dependFieldKey];
                  const age = parseIntOrZero(ans);

                  // Always set error or clear it, do not depend on meta.error
                  if (ageAtIndexDate > age) {
                    helpers.setError(
                      'Age at Index Date should not be greater than age at date of enrolment. Please check'
                    );
                  } else {
                    helpers.setError(undefined);
                  }
                } catch (err) {
                  // Optionally handle API errors
                  helpers.setError('Unable to validate age at index date.');
                }
              } else {
                // If no value selected, clear error
                helpers.setError(undefined);
              }
            }
            break;
          case 'cdst_score':
            const historyOfBowelSurgery = getIn(values, 'cdst_pdl.cdst_history_of_bowel_surgery');
            const historyOfPriorTNFAntagonistTherapy = getIn(
              values,
              'cdst_pdl.cdst_history_of_prior_TNF-antagonist_therapy'
            );
            const historyOfPriorFistulizingDisease = getIn(
              values,
              'cdst_pdl.cdst_history_of_prior_fistulizing_disease'
            );

            const cdstBaselineAlbuminAvailable = getIn(values, 'cdst_pdl.cdst_baseline_albumin_available');
            const cdstBaselineCRPAvailable = getIn(values, 'cdst_pdl.cdst_baseline_crp_available');

            const baselineAlbumin = getIn(values, 'cdst_pdl.cdst_baseline_albumin');
            const baselineCRP = getIn(values, 'cdst_pdl.cdst_baseline_crp');

            if(historyOfBowelSurgery === 'Unknown' || historyOfPriorTNFAntagonistTherapy === 'Unknown' || historyOfPriorFistulizingDisease === 'Unknown' || cdstBaselineAlbuminAvailable === 'No' || cdstBaselineCRPAvailable === 'No') {
              await helpers.setValue('null');
              return;
            }

            let cdstScore = 0;
            if (historyOfBowelSurgery === 'No') {
              cdstScore += 2;
            }
            if (historyOfPriorTNFAntagonistTherapy === 'No') {
              cdstScore += 3;
            }
            if (historyOfPriorFistulizingDisease === 'No') {
              cdstScore += 2;
            }
            if (baselineAlbumin !== 0) {
              const value = parseIntOrZero(baselineAlbumin);
              cdstScore += value * 0.4;
            }
            if (baselineCRP > 10) {
              cdstScore -= 3;
            } else if (baselineCRP >= 3 && baselineCRP <= 10) {
              cdstScore -= 0.5;
            }
            // Ensure cdstScore does not exceed 2 decimal points
            await helpers.setValue(cdstScore.toFixed(2).toString());
            break;
          default:
            break;
        }

        if (fieldKey.includes("HBI_SCORE")) {
          if (fieldConfig?.hbiFields) {
            const generalWellbeingRaw = getIn(values, fieldConfig?.hbiFields?.General_wellbeing);
            const complications = getIn(values, fieldConfig?.hbiFields?.Complications) || [];
            const complicationsCount = Array.isArray(complications) ? complications.length : 0;
            const numberLiquidStoolsRaw = getIn(values, fieldConfig?.hbiFields?.Number_of_liquid_stools_per_day);
            const abdominalPainRaw = getIn(values, fieldConfig?.hbiFields?.Abdominal_pain);
            const abdominalMassRaw = getIn(values, fieldConfig?.hbiFields?.Abdominal_mass);

            const isValueAvailable = (val) => val !== undefined && val !== null && val !== '';

            const requiredAvailable =
              isValueAvailable(generalWellbeingRaw) ||
              isValueAvailable(numberLiquidStoolsRaw) ||
              isValueAvailable(abdominalPainRaw) ||
              isValueAvailable(abdominalMassRaw);

            if (requiredAvailable) {
              const generalWellbeing = parseIntOrZero(generalWellbeingRaw);
              const Number_of_liquid_stools_per_day = parseIntOrZero(numberLiquidStoolsRaw);
              const Abdominal_pain = parseIntOrZero(abdominalPainRaw);
              const Abdominal_mass = parseIntOrZero(abdominalMassRaw);
              const hbiScore = generalWellbeing + complicationsCount + Number_of_liquid_stools_per_day + Abdominal_pain + Abdominal_mass;
              helpers.setValue(hbiScore);
            }else{
              helpers.setValue('');
            }
          }
        }
      }
    })()
  }, [trialSite, subject, values, fieldConfig, trialSite?.siteTrialData?.site?.id])

  useEffect(() => {
    (async () => {
      if (fieldKey === 'participantIdKey') {
        const currentValue = getIn(values, name);
        if (currentValue && subjectMasterId) {
          return;
        }
        const siteId = trialSite?.siteTrialData?.site?.id;
        if(!siteId) return;
        const piUser = getIn(values, 'pdl_Checklist.sitePIListKey') || getIn(values, 'demographic_data.sitePIListKey');

        let tempParticipantIdCacheKey = `tempParticipantId_${sponsorId}_${trialId}_${siteId}`;

        if (userSiteTrialRole === 'site_coordinator' && piUser) {
          tempParticipantIdCacheKey = `${tempParticipantIdCacheKey}_${piUser}`;
        }

        if (userSiteTrialRole === 'site_coordinator' && !piUser) {
          return;
        }

        if (apiCacheRef.current.has(tempParticipantIdCacheKey)) {
          const cached = apiCacheRef.current.get(tempParticipantIdCacheKey);
          helpers.setValue(cached);
          dispatch(setTempParticipantId(cached));
          return;
        }

        let tempParticipantId = await getSubjectTempParticipantId({
          sponsorId: sponsorId,
          trialId: trialId,
          siteId,
        }).unwrap();

        if (userSiteTrialRole === 'site_coordinator') {
          if (
            typeof tempParticipantId === "string" &&
            tempParticipantId.split("-").length === 3 &&
            piUser
          ) {
            const parts = tempParticipantId.split("-");
            parts[1] = piUser;
            tempParticipantId = parts.join("-");
          }
        }
        apiCacheRef.current.set(tempParticipantIdCacheKey, tempParticipantId);
        helpers.setValue(tempParticipantId);
        dispatch(setTempParticipantId(tempParticipantId));
      }
    })();
  }, [userSiteTrialRole, trialSite, values.pdl_Checklist?.sitePIListKey, trialSite?.siteTrialData?.site?.id, values, name, sponsorId, trialId, dispatch]);
  
  useEffect(() => {
    if (props.disabled) {
      setDisabled(true)
    }
  },[props.disabled])

  function _renderHelperText() {
    const [touched, error] = at(meta, 'touched', 'error');
    if (touched && error) {
      return error;
    } else {
      return ' ';
    }
  }

  const isRequired = useMemo(() => {
    if (Array.isArray(requiredDependOnFields) && requiredDependOnFields.length > 0) {
      for (const dep of requiredDependOnFields) {
        if (dep.requiredDepends) {
          const dependsKey = dep.requiredDepends.includes('.') ? dep.requiredDepends.split('.') : dep.requiredDepends;
          const dependsValue = dep.requiredDepends.includes('.')
            ? values[dependsKey[0]]?.[dependsKey[1]]
            : values[dependsKey];
          
          // Check if requiredValidationValue is provided - if so, use exact match validation
          // If not provided, check if any value exists (truthy check)
          if (dep.requiredValidationValue !== undefined && dep.requiredValidationValue !== null && dep.requiredValidationValue !== '') {
            if (dependsValue === dep.requiredValidationValue) {
              return true;
            }
          } else {
            // If requiredValidationValue is not provided, check if any value exists
            if (dependsValue !== undefined && dependsValue !== null && dependsValue !== '') {
              return true;
            }
          }
        }
      }
    } else if (requiredDepends) {
      const dependsKey = requiredDepends.includes('.') ? requiredDepends.split('.') : requiredDepends;
      const dependsValue = requiredDepends?.includes('.') ? values[dependsKey[0]]?.[dependsKey[1]] : values[dependsKey];
      
      // Check if requiredValidationValue is provided - if so, use exact match validation
      // If not provided, check if any value exists (truthy check)
      if (requiredValidationValue !== undefined && requiredValidationValue !== null && requiredValidationValue !== '') {
        if (dependsValue === requiredValidationValue) {
          return true;
        }
      } else {
        // If requiredValidationValue is not provided, check if any value exists
        if (dependsValue !== undefined && dependsValue !== null && dependsValue !== '') {
          return true;
        }
      }
    }

    if (required) {
      return true;
    }

    return false;
  }, [values, requiredDepends, requiredValidationValue, required, requiredDependOnFields]);

  const isDisabled = useMemo(() => {
    if (props.disabled) {
      return true;
    }

    if (Array.isArray(props.disabledDependsOnFields) && props.disabledDependsOnFields.length > 0) {
      for (const dep of props.disabledDependsOnFields) {
        if (dep.disabledDepends && dep.disabledValidationValue !== undefined) {
          const dependsKey = dep.disabledDepends.includes('.') ? dep.disabledDepends.split('.') : dep.disabledDepends;
          const dependsValue = dep.disabledDepends.includes('.')
            ? values[dependsKey[0]]?.[dependsKey[1]]
            : values[dependsKey];
          if (dependsValue === dep.disabledValidationValue) {
            return true;
          }
        }
      }
    } else if (props.disabledDepends && props.disabledValidationValue !== undefined) {
      const dependsKey = props.disabledDepends.includes('.') ? props.disabledDepends.split('.') : props.disabledDepends;
      const dependsValue = props.disabledDepends?.includes('.') ? values[dependsKey[0]]?.[dependsKey[1]] : values[dependsKey];
      if (dependsValue === props.disabledValidationValue) {
        return true;
      }
    }
    return false;
  }, [
    values,
    props.disabledDepends,
    props.disabledValidationValue,
    props.disabled,
    props.disabledDependsOnFields
  ]);

  return (
    <Box
      sx={{
        height: props.justifycontent ? 'auto' : '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: props.justifycontent ? 'start' : 'space-between',
      }}
    >
      <Box
        sx={{
          height: props.justifycontent ? 'auto' : '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ color: '#5b5b5b', fontWeight: props.labelFontWeight ?? 'medium' }} variant="subtitle1" gutterBottom>
          {props.label}
          {props.label && isRequired && <span style={{ color: 'red', fontSize: 18 }}>*</span>}
        </Typography>
        {props.info && (
          <Tooltip 
            title={props.info}
            componentsProps={{
              tooltip: {
                sx: { whiteSpace: 'pre-line' }
              }
            }}
          >
            <InfoOutlinedIcon sx={{ color: '#0f52b9', ml: 1, fontSize: 18, mb: 1 }} />
          </Tooltip>
        )}
      </Box>
      <TextField
        required={isRequired}
        type={props.type}
        multiline={props?.multiline}
        placeholder={props?.placeholder}
        fullWidth
        disabled={isDisabled}
        error={meta.touched && meta.error && true}
        helperText={_renderHelperText()}
        {...field}
        onKeyDown={(e) => {
          if (props.type==="number" && (e.key === "e" || e.key === "E" || e.key === "-" || e.key === "+")) {
            e.preventDefault()
          }
        }}
        InputProps={{
          inputProps: { min: 0, step: props?.step },
          endAdornment: props?.unit && (
            <InputAdornment position="end">
              <Typography variant="body2" color="grey[800]" fontWeight={500}>
                {props?.unit}
              </Typography>
            </InputAdornment>
          ),
        }}
        sx={{
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            display: 'none',
          },
        }}
        // {...rest}
      />
    </Box>
  );
}
